import React, {useEffect, useLayoutEffect, useRef} from 'react';
import PropTypes from 'prop-types';

import {Box, Portal, useMediaQuery, useMultiStyleConfig} from '@salesforce/retail-react-app/app/components/shared/ui';

import {useBlurOverlay} from '@amblique/king-living/app/contexts/blur-overlay-context';
import {useHeaderContext} from '@amblique/king-living/app/contexts/header-context';
import {MegaMenuDesktopLegacy} from './mega-menu-desktop-legacy';
import {MegaMenuDrawer} from '@amblique/king-living/app/components/mega-menu/desktop/mega-menu-drawer';
import {SEARCH_TAB_ID} from '@amblique/king-living/app/constants/navigation-drawer';
import {lockBodyScrollFreeze, unlockBodyScrollFreezeAndRestore} from '@amblique/king-living/app/utils/utils';

/**
 * Desktop mega menu.
 * Header 2.0 click drawer when CMS has Primary Items; otherwise the legacy
 * category hover row so unpublished Primary Items do not break layout.
 */
export const MegaMenuDesktop = ({
  isLegacyNav = false,
  isLoading = false,
  menuItems = [],
  navigationMenu,
  primaryTabs = [],
  ...props
}) => {
  const blurOverlay = useBlurOverlay();
  const wasOpenRef = useRef(false);
  const [isDesktop] = useMediaQuery('(min-width: 992px)');
  const {headerState, headerStyle, isSearchOpen, navDrawerTabId, closeNavDrawer, setMegaMenuOpen} =
    useHeaderContext();
  const isDrawerOpen = Boolean(navDrawerTabId);
  const styles = useMultiStyleConfig('MegaMenu', {
    headerState,
    isMenuOpen: isDrawerOpen,
    isSearchOpen,
    variant: headerStyle,
  });

  useEffect(() => {
    if (isLegacyNav || !isDesktop) {
      return undefined;
    }

    setMegaMenuOpen(isDrawerOpen && navDrawerTabId !== SEARCH_TAB_ID);

    return undefined;
  }, [isDesktop, isDrawerOpen, isLegacyNav, navDrawerTabId, setMegaMenuOpen]);

  // Page behind the drawer does not scroll; L1/L2/gallery still can.
  useLayoutEffect(() => {
    if (isLegacyNav || !isDesktop || !isDrawerOpen || typeof document === 'undefined') {
      return undefined;
    }

    const scrollY = window.scrollY ?? document.documentElement.scrollTop ?? 0;

    lockBodyScrollFreeze(scrollY);

    return () => {
      unlockBodyScrollFreezeAndRestore(scrollY);
    };
  }, [isDesktop, isDrawerOpen, isLegacyNav]);

  useEffect(() => {
    if (isLegacyNav || !isDesktop) {
      if (wasOpenRef.current) {
        blurOverlay.hide();
        wasOpenRef.current = false;
      }

      return undefined;
    }

    if (isDrawerOpen) {
      // Cover from the header row down (promo stays above). Hover does not close;
      // click the scrim (or Escape / X) does. Scrim is 30% black + 8px blur.
      blurOverlay.show({
        overlayTop: 'var(--nav-drawer-top, 0px)',
        backgroundColor: 'rgba(27, 26, 24, 0.30)',
        blur: '8px',
        onForceClose: closeNavDrawer,
      });
      wasOpenRef.current = true;

      return undefined;
    }

    if (wasOpenRef.current) {
      blurOverlay.hide();
      wasOpenRef.current = false;
    }

    return undefined;
  }, [blurOverlay, closeNavDrawer, isDesktop, isDrawerOpen, isLegacyNav]);

  if (isLegacyNav) {
    return (
      <MegaMenuDesktopLegacy
        isLoading={isLoading}
        menuItems={menuItems}
        navigationMenu={navigationMenu}
        {...props}
      />
    );
  }

  if (!isDesktop) {
    return null;
  }

  return (
    <Box className="mega-menu-desktop-wrapper" position="relative" {...props} {...styles.megaMenuDesktopWrapper}>
      <Portal>
        <MegaMenuDrawer isLoading={isLoading} primaryTabs={primaryTabs} />
      </Portal>
    </Box>
  );
};

MegaMenuDesktop.displayName = 'MegaMenuDesktop';
MegaMenuDesktop.propTypes = {
  isLegacyNav: PropTypes.bool,
  isLoading: PropTypes.bool,
  menuItems: PropTypes.array,
  navigationMenu: PropTypes.object,
  primaryTabs: PropTypes.array,
};
